import { useEffect, useState } from "react";
import stepsStyles from "../../components/Stepper/Steps/steps.module.css";
import styles from "./Modal.module.css";
import { FieldError, CedulaStatus, VehiculosField } from "../FieldHelpers";
import { useCedulaLookup } from "../../services/useCedulaLookup";
import { validateAutorizado } from "../../services/validationRules";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const EMPTY = {
  id: null,
  cedula: "",
  nombre: "",
  apellidos: "",
  relacion: "Familiar",
  vehiculos: [],
};

export default function Modal({
  open,
  onClose,
  formData,
  setFormData,
  selectedIndex,
}) {
  const [auth, setAuth] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { buscar, resultado, estado, reset } = useCedulaLookup();

  useEffect(() => {
    if (selectedIndex !== null) {
      const found = formData.autorizados.find((a) => a.id === selectedIndex);
      setAuth(found ? { ...EMPTY, ...found } : EMPTY);
    } else {
      setAuth(EMPTY);
    }
    setErrors({});
    reset();
    // formData.autorizados y reset se omiten a propósito: reset se recrea en
    // cada render (no memoizado en useCedulaLookup) y agregarlo dispararía
    // este efecto (y el reseteo del form) en cada render, no solo al abrir
    // el modal o cambiar de selección.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIndex]);

  useEffect(() => {
    if (resultado) {
      setAuth((prev) => ({
        ...prev,
        nombre: resultado.nombre || prev.nombre,
        apellidos: resultado.apellidos || prev.apellidos,
      }));
    }
  }, [resultado]);

  if (!open) return null;

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setAuth((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function handleSave(e) {
    e.preventDefault();
    e.stopPropagation();
    const errs = validateAutorizado(auth);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      autorizados: prev.autorizados.map((a) =>
        a.id === selectedIndex ? { ...auth, id: selectedIndex } : a,
      ),
    }));
    onClose();
  }

  return (
    <div className={styles.overlay} {...overlayClose(onClose)}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-3">Editar Autorizado</h2>

        {Object.keys(errors).length > 0 && (
          <div
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.3)",
              borderLeft: "3px solid #ef4444",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 12,
              color: "#f87171",
              fontSize: "0.8rem",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="fa-solid fa-triangle-exclamation" />
            Revisá los campos marcados.
          </div>
        )}

        <div className={styles["form-wrapper"]}>
          {/* Cédula */}
          <div className="row mb-2">
            <div className="form-group col-12">
              <label className="form-label">Cédula</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  name="cedula"
                  type="text"
                  className={`${stepsStyles["form-control"]} form-control`}
                  style={{ borderColor: errors.cedula ? "#ef4444" : undefined }}
                  placeholder="Ej: 123456789"
                  value={auth.cedula}
                  onChange={handleChange}
                  onBlur={() => buscar(auth.cedula)}
                />
                <button
                  type="button"
                  onClick={() => buscar(auth.cedula)}
                  disabled={estado === "buscando"}
                  style={{
                    padding: "0 10px",
                    background: "rgba(249,115,22,0.15)",
                    border: "1px solid rgba(249,115,22,0.4)",
                    borderRadius: 6,
                    color: "#f97316",
                    cursor: "pointer",
                    fontSize: "0.78rem",
                  }}
                >
                  {estado === "buscando" ? (
                    <i className="fa-solid fa-circle-notch fa-spin" />
                  ) : (
                    <i className="fa-solid fa-magnifying-glass" />
                  )}
                </button>
              </div>
              <CedulaStatus estado={estado} />
              <FieldError msg={errors.cedula} />
            </div>
          </div>

          {/* Nombre y Apellidos */}
          <div className="form-group row g-3 g-md-4 mb-2">
            <div className="col-12 col-md-6">
              <label className="form-label">Nombre</label>
              <input
                name="nombre"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{ borderColor: errors.nombre ? "#ef4444" : undefined }}
                placeholder="Nombre"
                value={auth.nombre}
                onChange={handleChange}
              />
              <FieldError msg={errors.nombre} />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Apellidos</label>
              <input
                name="apellidos"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{
                  borderColor: errors.apellidos ? "#ef4444" : undefined,
                }}
                placeholder="Apellidos"
                value={auth.apellidos}
                onChange={handleChange}
              />
              <FieldError msg={errors.apellidos} />
            </div>
          </div>

          {/* Relación y Vehículos */}
          <div className="form-group row g-3 g-md-4 mb-4">
            <div className="col-12 col-md-6">
              <label className="form-label">Relación</label>
              <select
                name="relacion"
                className={`${stepsStyles["form-select"]} form-select ${stepsStyles["form-control"]} form-control`}
                value={auth.relacion}
                onChange={handleChange}
              >
                <option value="Familiar">Familiar</option>
                <option value="Inquilino">Inquilino</option>
                <option value="Empleado">Empleado</option>
              </select>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Vehículos</label>
              <VehiculosField
                vehiculos={auth.vehiculos || []}
                errors={errors.vehiculos}
                controlClass={`${stepsStyles["form-control"]} form-control`}
                onChange={(vehiculos) => setAuth((prev) => ({ ...prev, vehiculos }))}
              />
            </div>
          </div>
        </div>

        <div className="d-flex gap-2">
          <button type="button" onClick={handleSave} className={styles.btn}>
            Guardar
          </button>
          <button type="button" onClick={onClose} className={styles.btn}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
